import { useEffect, useRef, useState, type FormEvent } from "react";
import { useAuth } from "../stores/auth";
import { api } from "../lib/api";
import { escapeKakao, isInApp, isKakaoInApp } from "../lib/inApp";

// ── 마케팅 베타 (구글 로그인) — 베타 종료 시 이 블록 전체 제거 ─────────
// 서버 토글(beta_google_login)이 꺼져 있으면 status.enabled=false 라 아무것도
// 렌더되지 않는다 — 떼어내기는 대시보드에서 즉시, 코드는 나중에.
interface BetaStatus {
  enabled: boolean;
  client_id: string;
}

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (cfg: object) => void;
          renderButton: (el: HTMLElement, cfg: object) => void;
        };
      };
    };
  }
}

function useBetaStatus(): BetaStatus | null {
  const [status, setStatus] = useState<BetaStatus | null>(null);
  useEffect(() => {
    let alive = true;
    api<{ ok: boolean; enabled: boolean; client_id: string }>("/agent/beta/status")
      .then((d) => {
        if (alive && d.ok) setStatus({ enabled: d.enabled, client_id: d.client_id });
      })
      .catch(() => {
        /* 베타 상태를 못 읽으면 버튼을 안 그린다 — 일반 로그인은 그대로 */
      });
    return () => {
      alive = false;
    };
  }, []);
  return status;
}

/** 구글 로그인 버튼 — 체크박스 없이 바로 누른다. 수집·이용 고지는 버튼 아래
 *  문구로 상시 노출하고, 로그인 행위가 곧 동의가 된다 (사용자 지정 2026-08-18). */
function GoogleBetaLogin({ clientId }: { clientId: string }) {
  const { loginWithToken } = useAuth();
  const [err, setErr] = useState("");
  const btnRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!btnRef.current) return;
    const render = () => {
      if (!window.google || !btnRef.current) return;
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: (resp: { credential?: string }) => {
          void (async () => {
            setErr("");
            try {
              const d = await api<{ ok: boolean; token?: string; user?: object; message?: string }>(
                "/agent/beta/google",
                { method: "POST", body: JSON.stringify({ credential: resp.credential || "" }) },
              );
              if (d.ok && d.token) loginWithToken(d.token, d.user as never);
              else setErr(d.message || "구글 로그인에 실패했습니다.");
            } catch (e) {
              setErr(e instanceof Error ? e.message : "구글 로그인에 실패했습니다.");
            }
          })();
        },
      });
      window.google.accounts.id.renderButton(btnRef.current, {
        theme: "outline", size: "large", width: 280, text: "continue_with", locale: "ko",
      });
    };
    if (window.google) {
      render();
      return;
    }
    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.onload = render;
    document.head.appendChild(script);
  }, [clientId, loginWithToken]);

  return (
    <div className="beta-login">
      <div className="beta-btn-wrap">
        <div ref={btnRef} className="beta-google-btn" />
      </div>
      <div className="beta-period">
        * 밸류에이션에이전트 페이지 베타서비스기간 (26년 8월 17일 – 26년 9월 25일 (예정))
      </div>
      <p className="beta-consent-note">
        구글 계정으로 로그인하면 「개인정보 보호법」 제15조에 따라, 베타 서비스 품질
        개선 및 통계·분석을 목적으로 계정 이메일, 대화 내용 및 서비스 이용 기록이
        수집·이용되는 것에 동의하게 됩니다.
      </p>
      {err && <p className="auth-error" role="alert">{err}</p>}
    </div>
  );
}
// ── 베타 블록 끝 ──────────────────────────────────────────────────────

/** 로그인 게이트 오버레이 — 베타 기간엔 구글 로그인이 첫 자리, DeepSearch
 *  계정(아이디·비밀번호)은 접힌 아래 단으로. 베타가 꺼져 있으면(구글 버튼이
 *  없으면) 접을 이유가 없으니 아이디 로그인을 처음부터 펼쳐 보인다. */
export default function LoginOverlay() {
  const { login, error } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  // 인앱 웹뷰(카카오톡 등)는 구글 OAuth 가 막히므로 아이디 로그인을 처음부터 펼친다
  const inApp = isInApp();
  const [idOpen, setIdOpen] = useState(inApp);
  const beta = useBetaStatus();
  const googleOn = !!(beta?.enabled && beta.client_id);
  // 베타 상태가 '입력 도중' 도착하면 폼이 접히며 치던 아이디가 사라진다
  // (레이스 — e2e 4분 타임아웃으로 발견, 실사용자도 같은 상황을 겪는다).
  // 무언가 입력했다면 레이아웃 전환이 와도 폼을 접지 않는다.
  const showIdForm = idOpen || !googleOn || !!(username || password);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    await login(username.trim(), password);
    setBusy(false);
  };

  return (
    <div className="auth-overlay" role="dialog" aria-modal="true" aria-label="로그인">
      <form className="auth-card" onSubmit={onSubmit}>
        <h2>DeepSearch 밸류에이션 에이전트</h2>
        {googleOn ? (
          <>
            <p className="auth-sub">구글 계정으로 간단하게 로그인하세요.</p>
            {/* 카카오 인앱 — 문구 없이 버튼만 (사용자 지정). 구글 버튼과 같은 폭으로
                나란히 두고, 누르면 외부 브라우저에서 열려 구글 로그인이 된다. */}
            {inApp && isKakaoInApp() && (
              <button type="button" className="auth-escape" onClick={escapeKakao}>
                외부 브라우저로 열기
              </button>
            )}
            {inApp && !isKakaoInApp() && (
              <div className="auth-inapp" role="alert">
                <p>인앱 브라우저에서는 구글 로그인이 제한됩니다. 우측 메뉴(⋯)에서 '다른 브라우저로 열기'를 눌러주세요.</p>
              </div>
            )}
            <GoogleBetaLogin clientId={beta!.client_id} />
            <div className="beta-divider"><span>또는 DeepSearch 계정</span></div>
            <button
              type="button"
              className="auth-fold"
              aria-expanded={showIdForm}
              onClick={() => setIdOpen((v) => !v)}
            >
              <span>DeepSearch 계정으로 로그인하세요.</span>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round"
                className={`auth-fold-caret${showIdForm ? " open" : ""}`} aria-hidden="true">
                <path d="m6 9 6 6 6-6" />
              </svg>
            </button>
          </>
        ) : (
          <p className="auth-sub">DeepSearch 계정으로 로그인하세요.</p>
        )}
        {showIdForm && (
          <>
            <label>
              아이디
              <input
                autoFocus={showIdForm && googleOn}
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            </label>
            <label>
              비밀번호
              <input
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </label>
            {error && <p className="auth-error" role="alert">{error}</p>}
            <button type="submit" disabled={busy || !username || !password}>
              {busy ? "로그인 중…" : "로그인"}
            </button>
          </>
        )}
      </form>
    </div>
  );
}
